import { ethers } from 'ethers';
import { PUBLIC_ENVIRONMENT } from '../../config/env';
import { ChainIds, pairAddress, routerAddress } from '../../constants';
import { useQuery } from '@tanstack/react-query';
import RouterAbi from '../abis/NewUniswapV2Router.json';

export type SwapType = 'Normal' | 'Encrypted' | 'Decrypted';
export type SwapHistoryData = {
  transactionHash: string;
  decryptedTxHash?: string;
  blockNumber: number;
  status: SwapType;
  id?: number;
};

const provider = new ethers.providers.JsonRpcProvider(
  PUBLIC_ENVIRONMENT.REACT_APP_ARBITRUM_RPC_URL,
);
const routerContractABI = RouterAbi.abi;
const routerContractAddress = routerAddress.get(ChainIds.ARBITRUM_DEMO);
const routerContract = new ethers.Contract(
  routerContractAddress,
  routerContractABI,
  provider,
);

const getSwapHistory = async (
  userAddress: string,
): Promise<SwapHistoryData[]> => {
  const pairContractAddress = pairAddress.get(ChainIds.ARBITRUM_DEMO);
  const swapEventSignature = ethers.utils.id(
    'Swap(address,uint256,uint256,uint256,uint256,address)',
  );
  const filter = {
    address: pairContractAddress,
    topics: [
      swapEventSignature,
      null,
      ethers.utils.hexZeroPad(userAddress, 32),
    ],
  };

  const fromBlock = 0;
  const toBlock = await provider.getBlockNumber();

  console.log(
    `Query ${pairContractAddress} block: ${fromBlock} to ${toBlock} ...`,
  );

  let totalLogs: ethers.providers.Log[] = [];
  const FILTER_BLOCK_STEP = 100000;
  for (let block = toBlock; block >= fromBlock; block -= FILTER_BLOCK_STEP) {
    let startBlock = block + 1 - FILTER_BLOCK_STEP;
    if (startBlock < fromBlock) startBlock = fromBlock;

    try {
      const logs: ethers.providers.Log[] = await provider.getLogs({
        ...filter,
        fromBlock: startBlock,
        toBlock: block - 1,
      });

      console.log(
        `Fetched logs from block: ${startBlock} to ${block - 1}, count: ${logs.length}`,
      );

      totalLogs = totalLogs.concat(logs);
    } catch (err) {
      console.log(err);
    }
  }

  return totalLogs.map((log) => ({
    transactionHash: log.transactionHash,
    blockNumber: log.blockNumber,
    status: 'Normal',
  }));
};

const getEncryptedSwapSubmissionHistory = async (
  userAddress: string,
): Promise<SwapHistoryData[]> => {
  const swapEventSignature = ethers.utils.id(
    'SwapQueued(uint256,address,bytes,string)',
  );
  const filter = {
    address: routerContractAddress,
    topics: [swapEventSignature, ethers.utils.hexZeroPad(userAddress, 32)],
  };

  const fromBlock = 0;
  const toBlock = await provider.getBlockNumber();

  console.log(
    `Query ${routerContractAddress} block: ${fromBlock} to ${toBlock} ...`,
  );

  let totalLogs: ethers.Event[] = [];
  const FILTER_BLOCK_STEP = 100000;
  for (let block = toBlock; block >= fromBlock; block -= FILTER_BLOCK_STEP) {
    let startBlock = block + 1 - FILTER_BLOCK_STEP;
    if (startBlock < fromBlock) startBlock = fromBlock;

    try {
      // const logs: ethers.providers.Log[] = await provider.getLogs({
      //   ...filter,
      //   fromBlock: startBlock,
      //   toBlock: block - 1,
      // });
      const events = await routerContract.queryFilter(
        filter,
        startBlock,
        block - 1,
      );

      console.log(
        `Fetched logs from block: ${startBlock} to ${block - 1}, count: ${events.length}`,
      );

      totalLogs = totalLogs.concat(events);
    } catch (err) {
      console.log(err);
    }
  }

  console.log(totalLogs);

  return totalLogs.map((log) => ({
    transactionHash: log.transactionHash,
    blockNumber: log.blockNumber,
    status: 'Encrypted',
    id: log?.args?.id?.toNumber(),
  }));
};

const getDecryptedSwapHistory = async (
  userAddress: string,
): Promise<SwapHistoryData[]> => {
  const swapEventSignature = ethers.utils.id('SwapExecuted(uint256,address)');
  const filter = {
    address: routerContractAddress,
    topics: [swapEventSignature, ethers.utils.hexZeroPad(userAddress, 32)],
  };

  const fromBlock = 0;
  const toBlock = await provider.getBlockNumber();

  console.log(
    `Query ${routerContractAddress} block: ${fromBlock} to ${toBlock} ...`,
  );

  let totalLogs: ethers.Event[] = [];
  const FILTER_BLOCK_STEP = 100000;
  for (let block = toBlock; block >= fromBlock; block -= FILTER_BLOCK_STEP) {
    let startBlock = block + 1 - FILTER_BLOCK_STEP;
    if (startBlock < fromBlock) startBlock = fromBlock;

    try {
      // const logs: ethers.providers.Log[] = await provider.getLogs({
      //   ...filter,
      //   fromBlock: startBlock,
      //   toBlock: block - 1,
      // });

      const events = await routerContract.queryFilter(
        filter,
        startBlock,
        block - 1,
      );

      console.log(
        `Fetched logs from block: ${startBlock} to ${block - 1}, count: ${events.length}`,
      );

      totalLogs = totalLogs.concat(events);
    } catch (err) {
      console.log(err);
    }
  }

  console.log(totalLogs);

  return totalLogs.map((log) => ({
    transactionHash: log.transactionHash,
    blockNumber: log.blockNumber,
    status: 'Decrypted',
    id: log?.args?.id?.toNumber(),
  }));
};

const getHistory = async (userAddress: string) => {
  const decrypted = await getDecryptedSwapHistory(userAddress);
  let encrypted = await getEncryptedSwapSubmissionHistory(userAddress);
  let normal = await getSwapHistory(userAddress);

  normal = normal.filter(
    (n) => !decrypted.some((d) => d.transactionHash === n.transactionHash),
  );

  decrypted.forEach((dec) => {
    const index = encrypted.findIndex((e) => e.id === dec.id);
    encrypted[index].status = 'Decrypted';
  });

  const all = encrypted.concat(normal);
  return all.sort((a, b) => b.blockNumber - a.blockNumber);
};

export const useHistory = (userAddress: string) => {
  return useQuery({
    queryKey: ['history', userAddress],
    queryFn: () => getHistory(userAddress),
    enabled: !!userAddress,
  });
};
